import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from '../../router/paths';
import {
  ArrowLeft, Loader2, ExternalLink, Plus, Trash2, Pencil, ImagePlus,
  Store, Grid3x3, MessageSquare, ShieldAlert, Eye, EyeOff, Send, CircleUserRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../ThemeProvider';
import { useUserData } from '../UserDataContext';
import { businessTokens } from './businessTheme';
import { BusinessStoreItemModal } from './BusinessStoreItemModal';
import { BusinessService } from '../../services/BusinessService';
import { openExternalUrl } from '../../utils/openExternal';
import { armPurchaseReturn } from '../../hooks/usePurchaseReturn';
import {
  BusinessProfile, BusinessFeedPost, BusinessStoreItem,
  STORE_ITEM_CAP, FEED_POST_MAX_LENGTH, BUSINESS_NAME_MAX_LENGTH,
  normalizeShopLink,
} from '../../types/business';

/**
 * A business profile: portfolio, posts, store.
 *
 * ONE component serves three audiences, because they are looking at the same
 * thing:
 *   • the owner        — plus a composer, store CRUD, and the settings sheet
 *   • a signed-in user — plus purchase tracking on Store taps
 *   • a logged-out visitor arriving on /business/:id — read-only, no tracking
 *
 * The differences are all additions to the same page rather than three pages,
 * so the public link cannot drift away from what the owner sees.
 */

type Tab = 'portfolio' | 'posts' | 'store';

export function BusinessProfileScreen({ onBack }: { onBack?: () => void }) {
  const { theme } = useTheme();
  const { userProfile, firebaseUser } = useUserData();
  const navigate = useNavigate();
  const { profileId } = useParams<{ profileId: string }>();
  const t = businessTokens(theme);

  const viewerId = String(userProfile?.id || firebaseUser?.uid || '');
  const isSignedIn = !!viewerId;

  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [feed, setFeed] = useState<BusinessFeedPost[]>([]);
  const [store, setStore] = useState<BusinessStoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('portfolio');

  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<BusinessStoreItem | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const isOwner = !!profile && profile.ownerId === viewerId;

  const load = useCallback(async () => {
    if (!profileId) return;
    const [p, f, s] = await Promise.all([
      BusinessService.getProfile(profileId),
      BusinessService.getFeed(profileId),
      BusinessService.getStore(profileId),
    ]);
    setProfile(p);
    setFeed(f);
    setStore(s);
    setLoading(false);
  }, [profileId]);

  useEffect(() => { void load(); }, [load]);

  // One view per mount, and never the owner's own. A counter that ticks every
  // time the owner checks their page tells them nothing.
  const viewCountedRef = useRef(false);
  useEffect(() => {
    if (!profile || viewCountedRef.current) return;
    if (profile.ownerId === viewerId) return;
    viewCountedRef.current = true;
    void BusinessService.recordProfileView(profile.id);
  }, [profile, viewerId]);

  /**
   * Tapping a product.
   *
   * Signed in: a purchase_checks document is written, the tracking code is
   * appended invisibly, the return listener is armed, and the shop opens.
   * Logged out: the shop just opens. No check document, no return prompt — that
   * whole flow is login-gated, and the rules enforce it independently.
   */
  const handleStoreTap = async (item: BusinessStoreItem) => {
    if (!profile) return;

    if (!isSignedIn) {
      if (!openExternalUrl(normalizeShopLink(item.purchaseLink))) {
        toast.error('Your browser blocked that link.');
      }
      return;
    }

    const res = await BusinessService.recordStoreTap(viewerId, profile, item);
    const url = res.data?.url || normalizeShopLink(item.purchaseLink);
    // Armed BEFORE the hop out, so the resume listener has something to act on
    // even if the app is killed while the user is away.
    if (res.data?.checkId) armPurchaseReturn(res.data.checkId);
    if (!openExternalUrl(url)) toast.error('Your browser blocked that link.');
  };

  // ── Shells ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ width: '100%', height: '100vh', background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 style={{ width: 30, height: 30, color: t.accent }} className="animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div
        style={{
          width: '100%', height: '100vh', background: t.bg, color: t.text,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24,
        }}
      >
        <Store style={{ width: 34, height: 34, color: t.textMuted }} />
        <p style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>This business is not available</p>
        <p style={{ margin: 0, fontSize: 13, color: t.textMuted, textAlign: 'center' }}>
          It may have been removed, or it is not currently visible.
        </p>
      </div>
    );
  }

  const suspended = profile.status === 'suspended';

  const tabButton = (id: Tab, label: string, Icon: any) => (
    <button
      key={id}
      type="button"
      onClick={() => setTab(id)}
      style={{
        flex: 1, height: 44, background: 'none', cursor: 'pointer',
        border: 'none', borderBottom: `2px solid ${tab === id ? t.accent : 'transparent'}`,
        color: tab === id ? t.text : t.textMuted,
        fontSize: 13, fontWeight: tab === id ? 700 : 600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}
    >
      <Icon style={{ width: 15, height: 15 }} />
      {label}
    </button>
  );

  return (
    <div style={{ width: '100%', height: '100vh', background: t.bg, overflowY: 'auto' }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ paddingTop: 'max(56px, calc(env(safe-area-inset-top) + 20px))' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px 12px' }}>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => (onBack ? onBack() : navigate(-1))}
            aria-label="Go back"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.text, padding: 0 }}
          >
            <ArrowLeft style={{ width: 24, height: 24 }} />
          </motion.button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0, fontSize: 20, fontWeight: 700, color: t.text,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {profile.businessName}
            </h1>
          </div>
          {isOwner && (
            <>
              {/* The other half of the "Manage <name>" button on the personal
                  profile: a deliberate switch back, not just a back arrow. The
                  arrow retraces history, which is wrong when the owner arrived
                  from a notification, a search result or a deep link — this
                  always lands on their own profile. */}
              <button
                type="button"
                onClick={() => navigate(PATHS.PROFILE)}
                aria-label="Switch back to your personal profile"
                title="Switch back to your personal profile"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4 }}
              >
                <CircleUserRound style={{ width: 19, height: 19 }} />
              </button>
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                aria-label="Business settings"
                title="Business settings"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4 }}
              >
                <Pencil style={{ width: 18, height: 18 }} />
              </button>
            </>
          )}
        </div>

        {suspended && (
          <div
            style={{
              margin: '0 16px 12px', padding: 12, borderRadius: 12,
              background: 'rgba(231,76,60,0.12)', border: `1px solid ${t.danger}44`,
              display: 'flex', gap: 8, alignItems: 'flex-start',
            }}
          >
            <ShieldAlert style={{ width: 16, height: 16, color: t.danger, flexShrink: 0, marginTop: 1 }} />
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: t.danger }}>This profile is suspended</p>
              <p style={{ margin: '3px 0 0', fontSize: 12, color: t.text }}>
                {profile.suspendReason || 'It is hidden from Explore, search and its public link.'}
              </p>
            </div>
          </div>
        )}

        {profile.description && (
          <p style={{ margin: '0 16px 12px', fontSize: 14, lineHeight: 1.5, color: t.textMuted }}>
            {profile.description}
          </p>
        )}

        <div style={{ padding: '0 16px 14px' }}>
          <button
            type="button"
            onClick={() => {
              if (!openExternalUrl(profile.shopLink)) toast.error('Your browser blocked that link.');
            }}
            style={{
              width: '100%', height: 46, borderRadius: 14, border: 'none',
              background: t.buttonBg, color: t.buttonText, fontSize: 14, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            Visit the shop
            <ExternalLink style={{ width: 15, height: 15 }} />
          </button>
        </div>

        <div style={{ display: 'flex', borderBottom: `1px solid ${t.border}`, padding: '0 8px' }}>
          {tabButton('portfolio', 'Portfolio', Grid3x3)}
          {tabButton('posts', 'Posts', MessageSquare)}
          {tabButton('store', 'Store', Store)}
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div style={{ padding: '16px 16px 110px', maxWidth: 640, margin: '0 auto' }}>
        {tab === 'portfolio' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {(profile.portfolioImages || []).map((url, i) => (
              <div key={i} style={{ aspectRatio: '1 / 1', borderRadius: 14, overflow: 'hidden', background: t.inset }}>
                <img src={url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ))}
          </div>
        )}

        {tab === 'posts' && (
          <>
            {isOwner && !suspended && <FeedComposer t={t} profileId={profile.id} ownerId={viewerId} onPosted={load} />}
            {feed.length === 0 ? (
              <EmptyState t={t} icon={MessageSquare} text="No posts yet." />
            ) : (
              feed.map(post => (
                <FeedPostCard
                  key={post.id}
                  t={t}
                  post={post}
                  canDelete={isOwner}
                  onDelete={async () => {
                    const res = await BusinessService.deleteFeedPost(profile.id, post.id);
                    if (!res.success) { toast.error(res.error || 'Could not delete that post.'); return; }
                    setFeed(prev => prev.filter(p => p.id !== post.id));
                  }}
                />
              ))
            )}
          </>
        )}

        {tab === 'store' && (
          <>
            {isOwner && !suspended && (
              <button
                type="button"
                onClick={() => { setEditingItem(null); setShowItemModal(true); }}
                disabled={store.length >= STORE_ITEM_CAP}
                style={{
                  width: '100%', height: 46, borderRadius: 14, border: `1px dashed ${t.accent}66`,
                  background: t.inset, color: t.accent, fontSize: 14, fontWeight: 700, marginBottom: 14,
                  cursor: store.length >= STORE_ITEM_CAP ? 'default' : 'pointer',
                  opacity: store.length >= STORE_ITEM_CAP ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <Plus style={{ width: 16, height: 16 }} />
                {store.length >= STORE_ITEM_CAP
                  ? `Store full (${STORE_ITEM_CAP} products)`
                  : `Add a product (${store.length}/${STORE_ITEM_CAP})`}
              </button>
            )}

            {store.length === 0 ? (
              <EmptyState t={t} icon={Store} text="No products listed yet." />
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {store.map(item => (
                  <StoreItemCard
                    key={item.id}
                    t={t}
                    item={item}
                    isOwner={isOwner}
                    disabled={suspended}
                    onTap={() => handleStoreTap(item)}
                    onEdit={() => { setEditingItem(item); setShowItemModal(true); }}
                    onDelete={async () => {
                      if (!window.confirm(`Remove "${item.title}" from your store?`)) return;
                      const res = await BusinessService.deleteStoreItem(profile.id, item.id);
                      if (!res.success) { toast.error(res.error || 'Could not remove that product.'); return; }
                      await load();
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* A passive line for logged-out visitors. Deliberately NOT an
            interruption in the Store-tap flow — whether a tap should prompt a
            signup is an open product question, so this stays out of the way. */}
        {!isSignedIn && (
          <div
            style={{
              marginTop: 24, padding: 14, borderRadius: 14,
              background: t.surface, border: `1px solid ${t.border}`, textAlign: 'center',
            }}
          >
            <p style={{ margin: 0, fontSize: 13, color: t.textMuted }}>
              You are viewing this on AfroYap.
            </p>
            <button
              type="button"
              onClick={() => navigate('/signup')}
              style={{
                marginTop: 10, height: 42, padding: '0 20px', borderRadius: 12, border: 'none',
                background: t.buttonBg, color: t.buttonText, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Create a free account
            </button>
          </div>
        )}
      </div>

      <BusinessStoreItemModal
        isOpen={showItemModal}
        onClose={() => setShowItemModal(false)}
        profileId={profile.id}
        ownerId={viewerId}
        editing={editingItem}
        currentCount={store.length}
        onSaved={load}
      />

      {showSettings && isOwner && (
        <OwnerSettingsSheet
          t={t}
          profile={profile}
          onClose={() => setShowSettings(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ── Pieces ───────────────────────────────────────────────────────────────────

function EmptyState({ t, icon: Icon, text }: { t: any; icon: any; text: string }) {
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <Icon style={{ width: 30, height: 30, color: t.textMuted, margin: '0 auto 10px', opacity: 0.5 }} />
      <p style={{ margin: 0, fontSize: 14, color: t.textMuted }}>{text}</p>
    </div>
  );
}

/**
 * The composer for off-timeline posts.
 *
 * Writes to business_profiles/{id}/feed — nothing here touches /yaps, and no
 * timeline-injection logic runs after it. These posts exist only on this page.
 */
function FeedComposer({ t, profileId, ownerId, onPosted }: {
  t: any; profileId: string; ownerId: string; onPosted: () => void;
}) {
  const [text, setText] = useState('');
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<'image' | 'video'>('image');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    if (!isVideo && !file.type.startsWith('image/')) { toast.error('Pick an image or a video.'); return; }
    if (file.size > 40 * 1024 * 1024) { toast.error('That file is over 40MB.'); return; }
    setBusy(true);
    try {
      setMediaUrl(await BusinessService.uploadBusinessMedia(ownerId, file, 'feed'));
      setMediaType(isVideo ? 'video' : 'image');
    } catch {
      toast.error('That file would not upload.');
    } finally {
      setBusy(false);
    }
  };

  const post = async () => {
    setBusy(true);
    const res = await BusinessService.createFeedPost(profileId, ownerId, {
      text,
      mediaUrls: mediaUrl ? [mediaUrl] : [],
      mediaType: mediaUrl ? mediaType : 'text',
    });
    setBusy(false);
    if (!res.success) { toast.error(res.error || 'Could not post that.'); return; }
    setText('');
    setMediaUrl(null);
    onPosted();
  };

  return (
    <div
      style={{
        background: t.surface, border: `1px solid ${t.border}`,
        borderRadius: 16, padding: 14, marginBottom: 16,
      }}
    >
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, FEED_POST_MAX_LENGTH))}
        placeholder="Post an update for people following your business"
        rows={3}
        style={{
          width: '100%', background: 'transparent', border: 'none', outline: 'none',
          color: t.text, fontSize: 14, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5,
        }}
      />
      {mediaUrl && (
        <div style={{ position: 'relative', marginTop: 8, borderRadius: 12, overflow: 'hidden' }}>
          {mediaType === 'video'
            ? <video src={mediaUrl} controls style={{ width: '100%', display: 'block' }} />
            : <img src={mediaUrl} alt="" style={{ width: '100%', display: 'block' }} />}
          <button
            type="button"
            onClick={() => setMediaUrl(null)}
            aria-label="Remove attachment"
            style={{
              position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: 999,
              background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          aria-label="Add an image or video"
          style={{
            width: 38, height: 38, borderRadius: 10, cursor: 'pointer',
            background: t.inset, border: `1px solid ${t.border}`, color: t.accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <ImagePlus style={{ width: 17, height: 17 }} />
        </button>
        <input ref={fileRef} type="file" accept="image/*,video/*" onChange={handleFile} style={{ display: 'none' }} />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: t.textMuted }}>{text.length}/{FEED_POST_MAX_LENGTH}</span>
        <button
          type="button"
          onClick={post}
          disabled={busy || (!text.trim() && !mediaUrl)}
          style={{
            height: 38, padding: '0 16px', borderRadius: 10, border: 'none',
            background: t.buttonBg, color: t.buttonText, fontSize: 13, fontWeight: 700,
            opacity: (busy || (!text.trim() && !mediaUrl)) ? 0.5 : 1,
            cursor: (busy || (!text.trim() && !mediaUrl)) ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {busy ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> : <Send style={{ width: 14, height: 14 }} />}
          Post
        </button>
      </div>
    </div>
  );
}

function FeedPostCard({ t, post, canDelete, onDelete }: {
  t: any; post: BusinessFeedPost; canDelete: boolean; onDelete: () => void;
}) {
  const media = post.mediaUrls?.[0];
  return (
    <div
      style={{
        background: t.surface, border: `1px solid ${t.border}`,
        borderRadius: 16, overflow: 'hidden', marginBottom: 12,
      }}
    >
      {media && (
        post.mediaType === 'video'
          ? <video src={media} controls playsInline style={{ width: '100%', display: 'block' }} />
          : <img src={media} alt="" loading="lazy" style={{ width: '100%', display: 'block' }} />
      )}
      {(post.text || canDelete) && (
        <div style={{ padding: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <p style={{ margin: 0, flex: 1, fontSize: 14, lineHeight: 1.55, color: t.text, whiteSpace: 'pre-wrap' }}>
            {post.text}
          </p>
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              aria-label="Delete post"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 2 }}
            >
              <Trash2 style={{ width: 15, height: 15 }} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function StoreItemCard({ t, item, isOwner, disabled, onTap, onEdit, onDelete }: {
  t: any; item: BusinessStoreItem; isOwner: boolean; disabled: boolean;
  onTap: () => void; onEdit: () => void; onDelete: () => void;
}) {
  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, overflow: 'hidden' }}>
      {item.imageUrl && (
        <img src={item.imageUrl} alt="" loading="lazy" style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }} />
      )}
      <div style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <p style={{ margin: 0, flex: 1, fontSize: 15, fontWeight: 700, color: t.text }}>{item.title}</p>
          {item.price && <span style={{ fontSize: 14, fontWeight: 700, color: t.accent }}>{item.price}</span>}
        </div>
        {item.description && (
          <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5, color: t.textMuted }}>{item.description}</p>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            type="button"
            onClick={onTap}
            disabled={disabled}
            style={{
              flex: 1, height: 42, borderRadius: 12, border: 'none',
              background: t.buttonBg, color: t.buttonText, fontSize: 14, fontWeight: 700,
              opacity: disabled ? 0.5 : 1, cursor: disabled ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            }}
          >
            Visit store
            <ExternalLink style={{ width: 14, height: 14 }} />
          </button>
          {isOwner && (
            <>
              <button
                type="button" onClick={onEdit} aria-label="Edit product"
                style={{
                  width: 42, height: 42, borderRadius: 12, cursor: 'pointer',
                  background: t.inset, border: `1px solid ${t.border}`, color: t.text,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Pencil style={{ width: 15, height: 15 }} />
              </button>
              <button
                type="button" onClick={onDelete} aria-label="Remove product"
                style={{
                  width: 42, height: 42, borderRadius: 12, cursor: 'pointer',
                  background: t.inset, border: `1px solid ${t.border}`, color: t.danger,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Trash2 style={{ width: 15, height: 15 }} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Owner settings.
 *
 * Two kinds of change sit side by side here, and the copy says which is which:
 * the personal-profile toggle takes effect immediately, while the name and shop
 * link go back through review because they are what a moderator approved. The
 * profile stays live and keeps its current details while that is pending.
 */
function OwnerSettingsSheet({ t, profile, onClose, onChanged }: {
  t: any; profile: BusinessProfile; onClose: () => void; onChanged: () => void;
}) {
  const [name, setName] = useState(profile.businessName);
  const [link, setLink] = useState(profile.shopLink);
  const [description, setDescription] = useState(profile.description || '');
  const [linked, setLinked] = useState(profile.showOnPersonalProfile !== false);
  const [images, setImages] = useState<string[]>(profile.portfolioImages || []);
  const [replacingSlot, setReplacingSlot] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const portfolioInputRef = useRef<HTMLInputElement>(null);
  const portfolioSlotRef = useRef(0);

  const replacePortfolioImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Pick an image.'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('That image is over 8MB.'); return; }
    const slot = portfolioSlotRef.current;
    setReplacingSlot(slot);
    try {
      const url = await BusinessService.uploadBusinessMedia(profile.ownerId, file, 'portfolio');
      setImages(prev => { const next = [...prev]; next[slot] = url; return next; });
    } catch {
      toast.error('That image would not upload.');
    } finally {
      setReplacingSlot(null);
    }
  };

  const portfolioChanged =
    images.length !== (profile.portfolioImages || []).length
    || images.some((url, i) => url !== profile.portfolioImages[i]);

  const nameOrLinkChanged =
    name.trim() !== profile.businessName
    || normalizeShopLink(link) !== normalizeShopLink(profile.shopLink);

  const save = async () => {
    setBusy(true);
    // Free-text and the toggle first: these never need review, so they land even
    // if the re-review request below is rejected.
    const patch = await BusinessService.updateProfile(profile.id, {
      description,
      showOnPersonalProfile: linked,
      ...(portfolioChanged ? { portfolioImages: images } : {}),
    });
    if (!patch.success) { setBusy(false); toast.error(patch.error || 'Could not save.'); return; }

    if (nameOrLinkChanged) {
      const review = await BusinessService.requestNameOrLinkEdit(profile.id, {
        businessName: name.trim() !== profile.businessName ? name : undefined,
        shopLink: normalizeShopLink(link) !== normalizeShopLink(profile.shopLink) ? link : undefined,
      });
      setBusy(false);
      if (!review.success) { toast.error(review.error || 'Could not submit that change.'); return; }
      toast.success('Saved. Your name and link change is with a moderator.');
    } else {
      setBusy(false);
      toast.success('Saved.');
    }
    onChanged();
    onClose();
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', background: t.inset, border: `1px solid ${t.border}`,
    borderRadius: 12, padding: '11px 13px', color: t.text, fontSize: 14,
    outline: 'none', fontFamily: 'inherit',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 700, color: t.text, margin: '14px 0 6px',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 999996, background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 500, maxHeight: '86vh', overflowY: 'auto',
          background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 20,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: t.text }}>Business settings</h2>

        <label style={labelStyle}>Business name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, BUSINESS_NAME_MAX_LENGTH))}
          style={inputStyle}
        />

        <label style={labelStyle}>Shop link</label>
        <input value={link} onChange={(e) => setLink(e.target.value)} inputMode="url" autoCapitalize="none" style={inputStyle} />

        {nameOrLinkChanged && (
          <p style={{ margin: '8px 0 0', fontSize: 12, color: t.warning }}>
            Changing your name or shop link goes back through review. Your profile stays live
            with its current details until a moderator decides.
          </p>
        )}

        <label style={labelStyle}>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 500))}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
        />
        <p style={{ margin: '6px 0 0', fontSize: 11, color: t.textMuted }}>
          Descriptions, portfolio images, posts and products save straight away, no review.
        </p>

        <label style={labelStyle}>Portfolio</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
          {images.map((url, slot) => (
            <button
              key={slot}
              type="button"
              onClick={() => { portfolioSlotRef.current = slot; portfolioInputRef.current?.click(); }}
              aria-label={`Replace portfolio image ${slot + 1}`}
              style={{
                position: 'relative', aspectRatio: '1 / 1', borderRadius: 10, overflow: 'hidden',
                padding: 0, cursor: 'pointer', border: `1px solid ${t.border}`, background: t.inset,
              }}
            >
              <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              {replacingSlot === slot && (
                <div
                  style={{
                    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Loader2 style={{ width: 16, height: 16, color: '#fff' }} className="animate-spin" />
                </div>
              )}
            </button>
          ))}
        </div>
        <input
          ref={portfolioInputRef}
          type="file"
          accept="image/*"
          onChange={replacePortfolioImage}
          style={{ display: 'none' }}
        />
        <p style={{ margin: '6px 0 0', fontSize: 11, color: t.textMuted }}>
          Tap an image to replace it.
        </p>

        <button
          type="button"
          onClick={() => setLinked(v => !v)}
          style={{
            width: '100%', marginTop: 16, padding: 12, borderRadius: 12, cursor: 'pointer',
            background: t.inset, border: `1px solid ${t.border}`, color: t.text,
            display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
          }}
        >
          {linked
            ? <Eye style={{ width: 17, height: 17, color: t.accent, flexShrink: 0 }} />
            : <EyeOff style={{ width: 17, height: 17, color: t.textMuted, flexShrink: 0 }} />}
          <span style={{ flex: 1, fontSize: 13 }}>
            {linked ? 'Linked from your personal profile' : 'Hidden from your personal profile'}
          </span>
        </button>
        <p style={{ margin: '6px 0 0', fontSize: 11, color: t.textMuted }}>
          Set per business, so you can link one and keep another to itself.
        </p>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1, height: 46, borderRadius: 12, cursor: 'pointer',
              background: t.inset, border: `1px solid ${t.border}`, color: t.text, fontSize: 14, fontWeight: 600,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            style={{
              flex: 1, height: 46, borderRadius: 12, border: 'none',
              background: t.buttonBg, color: t.buttonText, fontSize: 14, fontWeight: 700,
              opacity: busy ? 0.6 : 1, cursor: busy ? 'default' : 'pointer',
            }}
          >
            {busy ? 'Saving' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
